import type { FunctionCallingConfigFormToolField } from "components/formControls/FunctionCallingConfigControl/types";
import type { Tool } from "../types";
import { v4 as uuid } from "uuid";
import { transformToolToFunctionCallFormField } from "../utils/transformToolToFunctionCallFormField";
import {
  storeToolConfig,
  getStoredToolConfig,
  storeToolConfigs,
  restoreToolConfig,
} from "../utils/toolConfigStorage";

export const useToolSelection = (
  selectedTools: FunctionCallingConfigFormToolField[],
  onChange: (selectedTools: FunctionCallingConfigFormToolField[]) => void,
) => {
  const onToolSelect = (tool: Tool) => {
    if (tool.isSelected) {
      // Store config before removing
      const toolToRemove = selectedTools.find(
        (t) => t.entityId === tool.entityId,
      );

      if (toolToRemove) {
        storeToolConfig(toolToRemove.entityId, {
          description: toolToRemove.description,
          isApprovalRequired: toolToRemove.isApprovalRequired,
        });
      }

      onChange(selectedTools.filter((t) => t.entityId !== tool.entityId));
    } else {
      const storedConfig = getStoredToolConfig(tool.entityId);
      const newTool = transformToolToFunctionCallFormField({
        ...tool,
        id: uuid(),
      });
      const toolWithConfig = restoreToolConfig(newTool, storedConfig);

      onChange([...selectedTools, toolWithConfig]);
    }
  };

  const onSelectAll = (tools: Tool[], setSelectedTo: boolean) => {
    if (setSelectedTo) {
      const newSelectedTools = tools.filter((tool) => {
        return !selectedTools.some((t) => t.entityId === tool.entityId);
      });

      const toolsWithStoredConfig = newSelectedTools.map((tool) => {
        const storedConfig = getStoredToolConfig(tool.entityId);
        const newTool = transformToolToFunctionCallFormField({
          ...tool,
          id: uuid(),
        });

        return restoreToolConfig(newTool, storedConfig);
      });

      onChange([...selectedTools, ...toolsWithStoredConfig]);
    } else {
      // Store configs before removing
      const toolsToRemove = selectedTools.filter((tool) =>
        tools.some((t) => t.entityId === tool.entityId),
      );

      storeToolConfigs(toolsToRemove);

      onChange(
        selectedTools.filter(
          (tool) => !tools.some((t) => t.entityId === tool.entityId),
        ),
      );
    }
  };

  return { onToolSelect, onSelectAll };
};
